import { Landmark } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Advisory box for FINANCED deals when PPSA / bank / lender + admin fees stack above the review threshold
// on top of the all-in after-tax price. Advisory only — it does NOT affect the compliance score.
export const FinancingFeeWarning = ({ warning }) => {
  if (!warning) return null;
  const { fees = [], review_total, after_tax_total, total_financed } = warning;

  return (
    <div
      data-testid="financing-fee-warning"
      className="rounded-xl border border-warning/40 bg-warning-soft p-5"
    >
      <div className="flex items-start gap-3.5">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-warning/15 ring-1 ring-warning/40">
          <Landmark className="h-5 w-5 text-warning" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-3">
            <h4 className="font-heading text-sm font-800 uppercase tracking-wide text-warning">
              Financing fees to review
            </h4>
            <span className="rounded-full border border-warning/40 bg-background px-2.5 py-1 font-mono-plex text-[10px] font-600 uppercase tracking-[0.12em] text-warning">
              Review with dealer
            </span>
          </div>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            In Ontario, a PPSA (Personal Property Security Act) fee is a legitimate charge applied when you
            finance a vehicle. It covers the cost of registering a legal lien against the car in the
            province's public registry. The overall PPSA fee on a bill of sale typically ranges between{" "}
            <span className="font-700 text-foreground">$40 and $170</span>, depending on the lender and the
            length of the loan term.
          </p>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            <span className="font-700 text-foreground">Watch out for dealership markup:</span> while the
            PPSA fee itself is normal and mandatory, some dealerships bundle their own markups into this
            line item. Your lender fees here add up to{" "}
            <span className="font-700 text-foreground">{money(review_total)}</span> — if a dealership tries
            to charge you several hundred dollars (or more) explicitly for a "PPSA fee," they are inflating
            the cost. A lender contract has to be provided and the PPSA fees will be detailed there.
          </p>

          <div className="mt-3 space-y-1.5 rounded-md border border-warning/30 bg-background/40 p-3">
            {fees.map((f) => (
              <div key={f.label} className="flex items-center justify-between text-sm">
                <span className="text-foreground">{f.label}</span>
                <span className="font-mono-plex font-600 text-foreground">{money(f.amount)}</span>
              </div>
            ))}
            {after_tax_total != null && (
              <div className="mt-2 flex items-center justify-between border-t border-warning/20 pt-2 text-xs text-muted-foreground">
                <span>All-in after-tax price</span>
                <span className="font-mono-plex">{money(after_tax_total)}</span>
              </div>
            )}
            {total_financed != null && (
              <div className="flex items-center justify-between text-sm">
                <span className="font-700 text-foreground">Total financed amount</span>
                <span className="font-mono-plex font-700 text-foreground">{money(total_financed)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
